import { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Checkbox, Tag, Button } from "antd";
import { useTranslation } from "react-i18next";

import { bulkUnSubscribeAddon } from "../../../network/network";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError } from "../../../utils/errorHandler";
import { showWarningMessage } from "../../../utils/antdMessage";

const TrialAddonsModal = ({
  open,
  expiringAddons = [],
  onClose,
  setGlobalLoading,
}) => {
  const { refreshAuthUser, appConfig } = useContext(AppContext);
  const { t } = useTranslation();

  const isRTL = appConfig?.direction === "rtl";

  const getAddonKey = useCallback((a) => a?.addon_id ?? a?.id, []);

  const getLocalizedAddonName = useCallback(
    (a) => {
      if (!a) return "";
      return (isRTL ? a?.name_ar : a?.name) || a?.name || a?.name_ar || "";
    },
    [isRTL],
  );

  const parsePrice = useCallback((val) => {
    const n = parseFloat(val ?? 0);
    return Number.isNaN(n) ? 0 : n;
  }, []);

  const normalizeRefs = useCallback(
    (refs = []) => (refs || []).filter(Boolean),
    [],
  );

  const expiringIds = useMemo(
    () => expiringAddons.map(getAddonKey).filter(Boolean),
    [expiringAddons, getAddonKey],
  );

  const findExpiringAddonByKey = useCallback(
    (key) => expiringAddons.find((a) => getAddonKey(a) === key),
    [expiringAddons, getAddonKey],
  );

  // Computed dependents inside expiring list (used for disable logic)
  const getAffectsAddonsComputed = useCallback(
    (addon) => {
      const key = getAddonKey(addon);
      if (!key) return [];

      return expiringAddons.filter((a) => {
        const deps = normalizeRefs(a?.dependent_addons);
        return deps.some((depRef) => getAddonKey(depRef) === key);
      });
    },
    [expiringAddons, getAddonKey, normalizeRefs],
  );

  const getAffectsAddonsForUI = useCallback(
    (addon) =>
      normalizeRefs(addon?.required_by_addons).filter((x) => x?.is_subscribed),
    [normalizeRefs],
  );

  // Initial selected = all addons checked by default
  const initialSelected = useMemo(() => expiringIds, [expiringIds]);

  const [selectedAddonIds, setSelectedAddonIds] = useState(initialSelected);

  useEffect(() => {
    setSelectedAddonIds(initialSelected);
  }, [initialSelected]);

  const selectAddonIds = useCallback((ids) => {
    const clean = (ids || []).filter(Boolean);
    setSelectedAddonIds((prev) => Array.from(new Set([...prev, ...clean])));
  }, []);

  const deselectAddonIds = useCallback((ids) => {
    const remove = new Set((ids || []).filter(Boolean));
    setSelectedAddonIds((prev) => prev.filter((id) => !remove.has(id)));
  }, []);

  // ─────────────────────────────────────────────
  // Dependency traversal (uses expiringAddons map)
  // ─────────────────────────────────────────────
  const collectAllDependencies = useCallback(
    (addon) => {
      const visited = new Set();
      const stack = [addon];

      while (stack.length) {
        const current = stack.pop();
        const deps = normalizeRefs(current?.dependent_addons);

        deps.forEach((depRef) => {
          const depKey = getAddonKey(depRef);
          if (!depKey || visited.has(depKey)) return;

          visited.add(depKey);

          const depAddon = findExpiringAddonByKey(depKey);
          if (depAddon) stack.push(depAddon);
        });
      }

      return Array.from(visited);
    },
    [findExpiringAddonByKey, getAddonKey, normalizeRefs],
  );

  // Dependents for removal (only from expiring list)
  const collectAllDependents = useCallback(
    (addon) => {
      const visited = new Set();
      const stack = [addon];

      while (stack.length) {
        const current = stack.pop();
        const dependents = getAffectsAddonsComputed(current);

        dependents.forEach((depAddon) => {
          const depKey = getAddonKey(depAddon);
          if (!depKey || visited.has(depKey)) return;

          visited.add(depKey);
          stack.push(depAddon);
        });
      }

      return Array.from(visited);
    },
    [getAffectsAddonsComputed, getAddonKey],
  );

  const warnDisabledDependents = useCallback(
    (dependentsIds, parentAddon) => {
      const actuallyRemoved = (dependentsIds || []).filter((id) =>
        selectedAddonIds.includes(id),
      );
      if (!actuallyRemoved.length) return;

      const names = actuallyRemoved
        .map(findExpiringAddonByKey)
        .filter(Boolean)
        .map(getLocalizedAddonName)
        .filter(Boolean)
        .join(", ");

      if (!names) return;

      showWarningMessage(
        `${names} ${t("also_disabled_depends_on")} ${getLocalizedAddonName(
          parentAddon,
        )}.`,
        { duration: 6 },
      );
    },
    [findExpiringAddonByKey, getLocalizedAddonName, selectedAddonIds, t],
  );

  const handleAddonToggle = useCallback(
    (addon) => {
      const key = getAddonKey(addon);
      if (!key) return;

      const isSelected = selectedAddonIds.includes(key);

      // Select => also select all dependencies
      if (!isSelected) {
        const depsToSelect = collectAllDependencies(addon);
        selectAddonIds([key, ...depsToSelect]);
        return;
      }

      // Unselect => also unselect all dependents
      const dependentsToRemove = collectAllDependents(addon);
      deselectAddonIds([key, ...dependentsToRemove]);
      warnDisabledDependents(dependentsToRemove, addon);
    },
    [
      collectAllDependencies,
      collectAllDependents,
      deselectAddonIds,
      getAddonKey,
      selectAddonIds,
      selectedAddonIds,
      warnDisabledDependents,
    ],
  );

  const renderAddonTags = useCallback(
    (addons = []) => {
      const clean = (addons || []).filter(Boolean);
      if (!clean.length) return null;

      return clean
        .map((a) => {
          const id = getAddonKey(a);
          if (!id) return null;

          const full = findExpiringAddonByKey(id) || a;
          const label = getLocalizedAddonName(full) || `Addon ${id}`;

          return (
            <Tag key={id} color="red" style={{ marginInlineEnd: 0 }}>
              {label}
            </Tag>
          );
        })
        .filter(Boolean);
    },
    [findExpiringAddonByKey, getAddonKey, getLocalizedAddonName],
  );

  // ─────────────────────────────────────────────
  // Network actions
  // ─────────────────────────────────────────────
  const sendTrialDecision = useCallback(
    (payload) => {
      setGlobalLoading?.(true);

      bulkUnSubscribeAddon(payload)
        .then((response) => {
          const data = response?.data || response?.response?.data;

          if (data?.success === true) {
            NotificationWithIcon(
              "success",
              t("addons_updated"),
              t("addons_updated_successfully"),
            );
            onClose?.();
            refreshAuthUser?.();
            return;
          }

          handleApiError(response, t);
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => setGlobalLoading?.(false));
    },
    [onClose, refreshAuthUser, setGlobalLoading, t],
  );

  const handleKeepAddons = useCallback(() => {
    const wantedIds = Array.from(new Set(selectedAddonIds.filter(Boolean)));
    const wantedSet = new Set(wantedIds);

    const notWantedIds = expiringIds.filter((id) => !wantedSet.has(id));

    sendTrialDecision({
      wanted_addons: wantedIds,
      not_wanted_addons: notWantedIds,
    });
  }, [expiringIds, selectedAddonIds, sendTrialDecision]);

  const handleCancelAllAddons = useCallback(() => {
    sendTrialDecision({
      wanted_addons: [],
      not_wanted_addons: expiringIds,
    });
  }, [expiringIds, sendTrialDecision]);

  return (
    <Modal
      open={open}
      footer={null}
      centered
      width={650}
      closable={false}
      maskClosable={false}
      className="home-trial-modal"
      styles={{ body: { padding: 0 } }}
    >
      <div className="trialShell" dir={isRTL ? "rtl" : "ltr"}>
        {/* Header */}
        <div className="trialHeader">
          <div className="trialHeaderLeft">
            <div className="trialIcon">⚡</div>
            <div className="trialHeaderText">
              <div className="trialTitle">
                {t("trial_addons_expiring_today")}
              </div>
              <div className="trialSubtitle">{t("trial_addons_message")}</div>
            </div>
          </div>

          <div className="trialCount">
            {expiringAddons.length} {t("addons")}
          </div>
        </div>

        {/* List */}
        <div className="trialList">
          {expiringAddons.map((addon) => {
            const key = getAddonKey(addon);
            const isSelected = selectedAddonIds.includes(key);

            const affectsAddons = getAffectsAddonsForUI(addon);
            const price = parsePrice(addon?.price);

            return (
              <Button
                type="default"
                key={key}
                htmlType="button"
                className={`trialRow ${isSelected ? "isSelected" : ""}`}
                onClick={() => handleAddonToggle(addon)}
              >
                <div className="trialRowMain">
                  <div className="trialRowTitleLine">
                    <span className="trialRowTitleText">
                      {getLocalizedAddonName(addon)}
                    </span>

                    {price > 0 ? (
                      <span className="trialPrice">
                        <CurrencyFormatter value={price} />
                      </span>
                    ) : (
                      <Tag className="trialFreeTag">{t("free")}</Tag>
                    )}
                  </div>

                  {affectsAddons.length > 0 && (
                    <div className="trialInfoCard isAffects">
                      <div className="trialInfoDesc">
                        {t("addon_affects_short", {
                          addon: getLocalizedAddonName(addon),
                        })}
                      </div>

                      <div className="trialInfoTags">
                        {renderAddonTags(affectsAddons)}
                      </div>
                    </div>
                  )}
                </div>

                <span onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleAddonToggle(addon)}
                  />
                </span>
              </Button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="trialFooter">
          <Button type="text" danger onClick={handleCancelAllAddons}>
            {t("unsubscribe_all_addons")}
          </Button>

          <Button
            type="primary"
            disabled={selectedAddonIds.length === 0}
            onClick={handleKeepAddons}
          >
            {t("keep_addons")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default TrialAddonsModal;
